/// <amd-module name="Repos/Views/Import/Api" />
import * as React from "react";
import { GitImportRequest, GitRepository, ImportRepositoryValidation, VersionControlChangeType } from "Repos/Common/Contracts/Git";
import { CredentialSettings, ImportOptions, InitializeOptions, RepositoryType } from "Repos/Common/Types";
import { branchNameToRef } from "Repos/Common/Util/Ref";
import { IReposMessagesService } from "Repos/Messages/ReposMessagesService";
import { ServiceEndpoint } from "Repos/Views/Import/Contracts/ServiceEndpoint";
import * as Resources from "Repos/Views/Import/Resources";
import { getGitClient } from "Repos/Views/Import/RestClient/Git";
import { getServiceEndpointClient } from "Repos/Views/Import/RestClient/ServiceEndpoint";
import { format } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { issueRequest } from "VSS/Platform/Fetch";
import { IVssLocationService } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import { deserializeVssJsonObject } from "VSS/Platform/Util/Serialization";
import { combineUrlPaths } from "VSS/Platform/Util/Url";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Link } from "VSSUI/Link";
import { MessageCardSeverity } from "VSSUI/MessageCard";

function processMVCError(error: any): JSX.Element {
    const errorDocument = document.createElement("document");
    errorDocument.innerHTML = error.responseText.replace("<pre>", "<span>").replace("</pre>", "</span>");

    // Return the section from .more-info portion of the document. It is short
    // and reasonably descriptive of the error.
    const detailsElement = errorDocument.querySelector("#errorMessage");
    if (detailsElement) {
        throw <div dangerouslySetInnerHTML={{ __html: detailsElement.innerHTML }} />;
    } else {
        throw <span>{error.message}</span>;
    }
}

/**
 * generateCredential will create a set of credentials or PAT token. See below
 * for details about what the token will allow the caller to do.
 *
 *  Credential will be good for:
 *      Current User.
 *      Good for 365 days.
 *      Account specified in options.hostId.
 *      Access to the "vso.code-write" scope.
 *
 * @param pageContext Current requests page context, used to determine service endpoints.
 * @param options Details about the credentials that should be created.
 */
export async function generateCredential(
    pageContext: IVssPageContext,
    options: { description: string; hostId: string }
): Promise<{ Token: string } | null> {
    try {
        const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");
        const endpointUrl = locationService.routeUrl("ms.vss-tfs-web.user-settings-tokens-route", { action: "Edit" });

        const bodyData = {
            AccountMode: "SelectedAccounts",
            AuthorizationId: "",
            Description: options.description,
            ScopeMode: "SelectedScopes",
            SelectedAccounts: options.hostId,
            SelectedExpiration: "30",
            SelectedScopes: "vso.code_write"
        };

        const response = await issueRequest(endpointUrl, {
            body: JSON.stringify(bodyData),
            headers: { "Content-Type": "application/json" },
            method: "POST"
        });

        const responseText = await response.text();
        return deserializeVssJsonObject<{ Token: string }>(responseText);
    } catch (error) {
        throw processMVCError(error);
    }
}

/**
 * importRepository will start the import process based on the given details.
 *
 * @param pageContext Current requests page context, used to determine service endpoints.
 * @param projectId The GUID based identifier of the project that will hold the repository.
 * @param projectName The name of the project that will hold the repository.
 * @param options Details about the repository that is being imported.
 */
export async function importRepository(pageContext: IVssPageContext, options: ImportOptions): Promise<GitImportRequest> {
    const serviceEndpointClient = getServiceEndpointClient(pageContext);
    const gitClient = getGitClient(pageContext);

    try {
        const importSource = options.git
            ? { overwrite: false, url: options.git.cloneUrl }
            : {
                  importHistory: options.tfvc!.migrateDays > 0,
                  importHistoryDurationInDays: options.tfvc!.migrateDays,
                  path: options.tfvc!.path
              };
        let serviceEndpoint: ServiceEndpoint | undefined;

        // First perform validation on the import request.
        const validation: ImportRepositoryValidation = options.git
            ? {
                  gitSource: importSource,
                  password: options.git.authPassword || null,
                  username: options.git.authUsername || null
              }
            : ({
                  tfvcSource: importSource
              } as any);

        await gitClient.validateRemoteRepository(validation as any, options.projectName);

        // Second create the repository if the user supplied a new repository name.
        if (options.createRepository) {
            const gitRepositoryToCreate = {
                name: options.repositoryName,
                project: {
                    id: options.projectId,
                    name: options.projectName
                } as any
            } as any;
            
            try {
                const telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
                telemetryService.publishEvent("Repositories", 
                                                "ImportRepository", 
                                                gitRepositoryToCreate);
            } catch {
                //ignore
            }

            await gitClient.createRepository(
                gitRepositoryToCreate,
                options.projectName
            );
        }

        // Since the import requires a username/password we will setup a service endpoint
        if (options.git && (options.git.authUsername || options.git.authPassword)) {
            serviceEndpoint = await serviceEndpointClient.createServiceEndpoint(
                {
                    authorization: {
                        parameters: {
                            password: options.git.authPassword || null,
                            username: options.git.authUsername || null
                        },
                        scheme: "UsernamePassword"
                    },
                    name: Math.random().toString(36).substring(7),
                    type: RepositoryType.git,
                    url: options.git.cloneUrl
                } as any,
                options.projectId
            );
        }

        // Start the import now that all the required state has been configured.
        const result = await gitClient.createImportRequest(
            {
                parameters: {
                    deleteServiceEndpointAfterImportIsDone: true,
                    gitSource: options.git ? importSource : null,
                    tfvcSource: !options.git ? importSource : null,
                    serviceEndpointId: serviceEndpoint && serviceEndpoint.id
                }
            } as any,
            options.projectId,
            options.repositoryName
        );

        return result;
    } catch (error) {
        const message = error.toString();
        if (message.indexOf("404") >= 0) {
            throw (
                <FormatComponent elementType="div" format="{0}{1}{2}{3}">
                    <div>{Resources.ImportFailed}</div>
                    <br />
                    {(options.type === RepositoryType.git ? Resources.GitImportFailed : Resources.TFVCImportFailed)
                        .split("<br />")
                        .map((value, index) => (
                            <span key={index}>
                                {value}
                                <br />
                            </span>
                        ))}
                    <Link
                        href={
                            options.type === RepositoryType.git
                                ? "https://go.microsoft.com/fwlink/?linkid=836846"
                                : "https://go.microsoft.com/fwlink/?linkid=839462"
                        }
                        target="_blank"
                    >
                        {Resources.LearnMoreError}
                    </Link>
                </FormatComponent>
            );
        } else {
            throw <span>{error.message}</span>;
        }
    }
}

/**
 * initializeRepository is used to populate an empty Git repository with a small
 * set of default files. It may include a readme.md and .gitignore based on an
 * application template.
 *
 * @param pageContext Current requests page context, used to determine service endpoints.
 * @param options Set of options that define how the repository will be initialized.
 */
export async function initializeRepository(
    pageContext: IVssPageContext,
    options: InitializeOptions
): Promise<{ repository: GitRepository | { webUrl: string } }> {
    if (options.type === RepositoryType.git) {
        try {
            const gitClient = getGitClient(pageContext);
            const changes: any[] = [];
            const comments: string[] = [];
            let repository = options.repository;            

            // Second create the repository if the user supplied a new repository name.
            if (options.createRepository) {
                const gitRepositoryToCreate = {
                    name: options.repositoryName,
                    project: {
                        id: options.projectId,
                        name: options.projectName
                    } as any
                } as any;
                
                try {
                    const telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
                    telemetryService.publishEvent("Repositories", 
                                                "CreateRepository", 
                                                gitRepositoryToCreate);
                } catch {
                    // ignore
                }

                repository = await gitClient.createRepository(
                    gitRepositoryToCreate,
                    options.projectName
                );
            }

            // If a readme.md was included, add it to the comments and the change list.
            if (options.readme) {
                comments.push(format(Resources.CommentReadMe, "README.md"));
                changes.push({
                    changeType: VersionControlChangeType.Add,
                    item: { path: "/README.md" },
                    newContentTemplate: { name: "README.md", type: "readme" }
                });
            }

            // If a .gitignore was inclueded add it to the comments and the change list.
            if (options.application) {
                comments.push(format(Resources.CommentGitIgnore, options.application.name));
                changes.push({
                    changeType: VersionControlChangeType.Add,
                    item: { path: "/.gitignore" },
                    newContentTemplate: { name: options.application.id, type: "gitignore" }
                });
            }

            if (changes.length) {
                try {
                    await gitClient.createPush(
                        {
                            commits: [{ comment: comments.join(", "), changes }],
                            refUpdates: [
                                {
                                    name: branchNameToRef(options.defaultGitBranchName || "main"),
                                    oldObjectId: "0000000000000000000000000000000000000000"
                                }
                            ],
                            repository: options.repository
                        } as any,
                        repository!.id,
                        options.projectName
                    );
                } catch (exception) {
                    // Since we created the repository we will just "succeed" with a warning.
                    if (options.createRepository) {
                        const messagesService = pageContext.getService<IReposMessagesService>("IReposMessagesService");
                        messagesService.showMessage({ renderMessage: () => exception.message, severity: MessageCardSeverity.Error }, true);
                    } else {
                        throw exception;
                    }
                }
            }

            return { repository: repository! };
        } catch (error) {
            throw <span>{error.message}</span>;
        }
    } else {
        try {
            const verificationInput = document.querySelector<HTMLInputElement>("input[name=__RequestVerificationToken]");
            const hostPath = pageContext.getService<IVssPageService>("IVssPageService").getData()!.hostPath || "";
            const endpointUrl = combineUrlPaths(encodeURI(hostPath), `${options.projectId}/_api/_versioncontrol/createProjectFolder?__v=5`);

            await issueRequest(endpointUrl, {
                body: `projectUri=vstfs:///Classification/TeamProject/${options.projectId}&__RequestVerificationToken=${encodeURIComponent(
                    verificationInput ? verificationInput.value : ""
                )}`,
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                method: "POST"
            });

            return { repository: { webUrl: combineUrlPaths(encodeURI(hostPath), `${encodeURIComponent(options.projectName)}/_versionControl`) } };
        } catch (error) {
            throw processMVCError(error);
        }
    }
}
